// ✅ Analytics Utility Functions for GA4 + GTM
import { logAnalyticsEvent } from '../firebase';

/**
 * Helper function to push events to GTM dataLayer
 * @param {string} event - Event name
 * @param {Object} parameters - Event parameters
 */
const pushToDataLayer = (event, parameters = {}) => {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: event,
      ...parameters
    });
  } catch (error) {
    console.warn('DataLayer push error:', error);
  }
};

/**
 * Track page views automatically
 * @param {string} pageName - Name of the page
 * @param {string} pageTitle - Title of the page
 * @param {string} pageLocation - Full URL of the page
 */
export const trackPageView = (pageName, pageTitle, pageLocation) => {
  const eventParams = {
    page_title: pageTitle,
    page_location: pageLocation,
    page_name: pageName
  };
  
  try {
    // Push to GTM dataLayer
    pushToDataLayer('page_view', eventParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent('page_view', eventParams);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track user sign up events with acquisition data
 * @param {string} method - Sign up method (email, google, apple)
 */
export const trackSignUp = (method) => {
  try {
    // Get stored acquisition data to include in sign_up event
    const acquisitionData = getStoredAcquisitionData();
    
    const eventParams = {
      method: method
    };
    
    // Add acquisition parameters if available
    if (acquisitionData) {
      if (acquisitionData.utm_source) eventParams.utm_source = acquisitionData.utm_source;
      if (acquisitionData.utm_medium) eventParams.utm_medium = acquisitionData.utm_medium;
      if (acquisitionData.utm_campaign) eventParams.utm_campaign = acquisitionData.utm_campaign;
      if (acquisitionData.referrer_source) eventParams.referrer_source = acquisitionData.referrer_source;
      if (acquisitionData.landing_page) eventParams.landing_page = acquisitionData.landing_page;
    }
    
    // Push to GTM dataLayer
    pushToDataLayer('sign_up', eventParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent('sign_up', eventParams);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track user login events
 * @param {string} method - Login method (email, google, apple)
 */
export const trackLogin = (method) => {
  const eventParams = {
    method: method
  };
  
  try {
    // Push to GTM dataLayer
    pushToDataLayer('login', eventParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent('login', eventParams);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track book addition events
 * @param {string} method - How the book was added (manual, ai, google_books, open_library)
 * @param {string} category - Book category (fiction, nonfiction, etc.)
 */
export const trackBookAdded = (method, category) => {
  const eventParams = {
    method: method,
    category: category
  };
  
  try {
    // Push to GTM dataLayer
    pushToDataLayer('book_added', eventParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent('book_added', eventParams);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track reading status changes
 * @param {string} status - New reading status (reading, done, want_to_read)
 * @param {string} category - Book category
 */
export const trackReadingStatusChange = (status, category) => {
  const eventParams = {
    status: status,
    category: category
  };
  
  try {
    // Push to GTM dataLayer
    pushToDataLayer('reading_status_changed', eventParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent('reading_status_changed', eventParams);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track book search events
 * @param {string} searchTerm - What the user searched for
 * @param {string} source - Search source (google_books, open_library)
 */
export const trackBookSearch = (searchTerm, source) => {
  const eventParams = {
    search_term: searchTerm,
    source: source
  };
  
  try {
    // Push to GTM dataLayer
    pushToDataLayer('search', eventParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent('search', eventParams);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track profile view events
 * @param {string} profileType - Type of profile viewed (own, public)
 */
export const trackProfileView = (profileType) => {
  const eventParams = {
    profile_type: profileType
  };
  
  try {
    // Push to GTM dataLayer
    pushToDataLayer('profile_view', eventParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent('profile_view', eventParams);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track affiliate link clicks
 * @param {string} platform - Affiliate platform (bookshop, amazon, etc.)
 * @param {string} bookTitle - Title of the book
 */
export const trackAffiliateClick = (platform, bookTitle) => {
  const eventParams = {
    platform: platform,
    book_title: bookTitle
  };
  
  try {
    // Push to GTM dataLayer
    pushToDataLayer('affiliate_click', eventParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent('affiliate_click', eventParams);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track follow user events
 * @param {string} targetUserId - ID of user being followed
 * @param {string} targetUsername - Username of user being followed
 */
export const trackFollowUser = (targetUserId, targetUsername) => {
  const eventParams = {
    target_user_id: targetUserId,
    target_username: targetUsername
  };
  
  try {
    // Push to GTM dataLayer
    pushToDataLayer('follow_user', eventParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent('follow_user', eventParams);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track unfollow user events
 * @param {string} targetUserId - ID of user being unfollowed
 * @param {string} targetUsername - Username of user being unfollowed
 */
export const trackUnfollowUser = (targetUserId, targetUsername) => {
  const eventParams = {
    target_user_id: targetUserId,
    target_username: targetUsername
  };
  
  try {
    // Push to GTM dataLayer
    pushToDataLayer('unfollow_user', eventParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent('unfollow_user', eventParams);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track block user events
 * @param {string} targetUserId - ID of user being blocked
 * @param {string} targetUsername - Username of user being blocked
 */
export const trackBlockUser = (targetUserId, targetUsername) => {
  const eventParams = {
    target_user_id: targetUserId,
    target_username: targetUsername
  };
  
  try {
    // Push to GTM dataLayer
    pushToDataLayer('block_user', eventParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent('block_user', eventParams);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track followers/following modal opened
 * @param {string} modalType - Type of modal ('followers' or 'following')
 * @param {string} profileUsername - Username of profile being viewed
 */
export const trackFollowersModalOpened = (modalType, profileUsername) => {
  const eventParams = {
    modal_type: modalType,
    profile_username: profileUsername
  };
  
  try {
    // Push to GTM dataLayer
    pushToDataLayer(`${modalType}_modal_opened`, eventParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent(`${modalType}_modal_opened`, eventParams);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track custom events with flexible parameters
 * @param {string} eventName - Name of the event
 * @param {Object} parameters - Event parameters
 */
export const trackCustomEvent = (eventName, parameters = {}) => {
  try {
    // Push to GTM dataLayer
    pushToDataLayer(eventName, parameters);
    
    // Track in Firebase Analytics
    logAnalyticsEvent(eventName, parameters);
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Capture user acquisition source from URL parameters and referrer
 * @returns {Object} Acquisition data including UTM parameters, referrer, and landing page
 */
export const captureAcquisitionSource = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = document.referrer;
    
    // Determine referrer source
    let referrerSource = 'direct';
    if (referrer) {
      if (referrer.includes('google.com')) referrerSource = 'google';
      else if (referrer.includes('facebook.com') || referrer.includes('fb.com')) referrerSource = 'facebook';
      else if (referrer.includes('twitter.com') || referrer.includes('t.co')) referrerSource = 'twitter';
      else if (referrer.includes('instagram.com')) referrerSource = 'instagram';
      else if (referrer.includes('linkedin.com')) referrerSource = 'linkedin';
      else if (referrer.includes('pinterest.com')) referrerSource = 'pinterest';
      else referrerSource = 'referral';
    }
    
    return {
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      utm_content: urlParams.get('utm_content') || null,
      utm_term: urlParams.get('utm_term') || null,
      referrer: referrer || 'direct',
      referrer_source: referrerSource,
      landing_page: window.location.pathname,
      landing_url: window.location.href
    };
  } catch (error) {
    console.warn('Error capturing acquisition source:', error);
    return {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
      referrer: 'unknown',
      referrer_source: 'unknown',
      landing_page: '/',
      landing_url: window.location.href
    };
  }
};

/**
 * Store acquisition data in localStorage for later retrieval during signup
 * This preserves the original landing page and UTM parameters
 */
export const storeAcquisitionData = () => {
  try {
    // Only store if we don't already have acquisition data
    const existing = localStorage.getItem('gutenstack_acquisition');
    if (existing) return;
    
    const acquisitionData = captureAcquisitionSource();
    acquisitionData.first_visit_timestamp = new Date().toISOString();
    
    localStorage.setItem('gutenstack_acquisition', JSON.stringify(acquisitionData));
    
    // Track the landing page visit
    const firstVisitParams = {
      landing_page: acquisitionData.landing_page,
      utm_source: acquisitionData.utm_source,
      utm_medium: acquisitionData.utm_medium,
      utm_campaign: acquisitionData.utm_campaign,
      referrer_source: acquisitionData.referrer_source
    };
    
    // Push to GTM dataLayer
    pushToDataLayer('first_visit', firstVisitParams);
    
    // Track in Firebase Analytics
    logAnalyticsEvent('first_visit', firstVisitParams);
  } catch (error) {
    console.warn('Error storing acquisition data:', error);
  }
};

/**
 * Retrieve stored acquisition data from localStorage
 * @returns {Object|null} Stored acquisition data or null if not found
 */
export const getStoredAcquisitionData = () => {
  try {
    const stored = localStorage.getItem('gutenstack_acquisition');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Error retrieving acquisition data:', error);
    return null;
  }
};

/**
 * Clear stored acquisition data after successful signup
 */
export const clearAcquisitionData = () => {
  try {
    localStorage.removeItem('gutenstack_acquisition');
  } catch (error) {
    console.warn('Error clearing acquisition data:', error);
  }
};

// ===================================================================
// 🆕 SIGNUP FUNNEL TRACKING (Added for conversion tracking)
// ===================================================================

/**
 * Initialize signup funnel state in localStorage
 * @param {string} step - Current funnel step
 * @param {string} method - Signup method (email, google, or null)
 */
export const initSignupFunnel = (step, method = null) => {
  try {
    const funnelState = {
      current_step: step,
      method: method,
      started_at: new Date().toISOString(),
      account_created_at: null,
      completed: false
    };
    localStorage.setItem('gutenstack_signup_state', JSON.stringify(funnelState));
  } catch (error) {
    console.warn('Error initializing signup funnel:', error);
  }
};

/**
 * Update signup funnel state
 * @param {string} step - New step
 * @param {Object} additionalData - Additional data to merge
 */
export const updateSignupFunnel = (step, additionalData = {}) => {
  try {
    const existing = localStorage.getItem('gutenstack_signup_state');
    if (!existing) return;
    
    const state = JSON.parse(existing);
    const updatedState = {
      ...state,
      current_step: step,
      ...additionalData
    };
    localStorage.setItem('gutenstack_signup_state', JSON.stringify(updatedState));
  } catch (error) {
    console.warn('Error updating signup funnel:', error);
  }
};

/**
 * Get current signup funnel state
 * @returns {Object|null} Current funnel state or null
 */
export const getSignupFunnelState = () => {
  try {
    const state = localStorage.getItem('gutenstack_signup_state');
    return state ? JSON.parse(state) : null;
  } catch (error) {
    console.warn('Error getting signup funnel state:', error);
    return null;
  }
};

/**
 * Clear signup funnel state (call when signup completed)
 */
export const clearSignupFunnel = () => {
  try {
    localStorage.removeItem('gutenstack_signup_state');
  } catch (error) {
    console.warn('Error clearing signup funnel:', error);
  }
};

/**
 * Track signup page view
 * @param {string} tab - Current tab (signup or signin)
 */
export const trackSignupPageView = (tab) => {
  const acquisitionData = getStoredAcquisitionData();
  
  const eventParams = {
    tab: tab,
    utm_source: acquisitionData?.utm_source || null,
    utm_campaign: acquisitionData?.utm_campaign || null,
    referrer_source: acquisitionData?.referrer_source || 'direct'
  };
  
  try {
    pushToDataLayer('signup_page_viewed', eventParams);
    logAnalyticsEvent('signup_page_viewed', eventParams);
    
    // Initialize funnel state if on signup tab
    if (tab === 'signup') {
      initSignupFunnel('page_viewed');
    }
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track signup method selection
 * @param {string} method - Selected method (email or google)
 */
export const trackSignupMethodSelected = (method) => {
  const eventParams = { method: method };
  
  try {
    pushToDataLayer('signup_method_selected', eventParams);
    logAnalyticsEvent('signup_method_selected', eventParams);
    
    updateSignupFunnel('method_selected', { method: method });
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track when user starts filling signup form (email only)
 */
export const trackSignupFormStarted = () => {
  const eventParams = { method: 'email' };
  
  try {
    pushToDataLayer('signup_form_started', eventParams);
    logAnalyticsEvent('signup_form_started', eventParams);
    
    updateSignupFunnel('form_started');
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track signup attempt (before success)
 * @param {string} method - Signup method (email or google)
 */
export const trackSignupAttempted = (method) => {
  const eventParams = { method: method };
  
  try {
    pushToDataLayer('signup_attempted', eventParams);
    logAnalyticsEvent('signup_attempted', eventParams);
    
    updateSignupFunnel('account_attempted');
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track when email verification is sent
 */
export const trackEmailVerificationSent = () => {
  try {
    pushToDataLayer('email_verification_sent', {});
    logAnalyticsEvent('email_verification_sent', {});
    
    updateSignupFunnel('verification_sent');
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track when onboarding starts
 * @param {string} signupMethod - Original signup method
 */
export const trackOnboardingStarted = (signupMethod) => {
  const eventParams = { signup_method: signupMethod };
  
  try {
    pushToDataLayer('onboarding_started', eventParams);
    logAnalyticsEvent('onboarding_started', eventParams);
    
    updateSignupFunnel('onboarding_started');
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track first book added during signup flow
 * @param {string} method - How book was added (manual, ai, search)
 * @param {string} category - Book category
 */
export const trackFirstBookAdded = (method, category) => {
  const eventParams = {
    method: method,
    category: category
  };
  
  try {
    pushToDataLayer('first_book_added', eventParams);
    logAnalyticsEvent('first_book_added', eventParams);
    
    updateSignupFunnel('first_book_added');
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track signup journey completion
 * @param {string} signupMethod - Original signup method
 */
export const trackSignupCompleted = (signupMethod) => {
  const state = getSignupFunnelState();
  
  // Calculate total time
  let totalMinutes = null;
  if (state?.started_at) {
    const startTime = new Date(state.started_at);
    const now = new Date();
    totalMinutes = Math.round((now - startTime) / (1000 * 60));
  }
  
  const eventParams = {
    signup_method: signupMethod,
    total_time_minutes: totalMinutes
  };
  
  try {
    pushToDataLayer('signup_completed', eventParams);
    logAnalyticsEvent('signup_completed', eventParams);
    
    // Clear funnel state
    clearSignupFunnel();
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track signup abandonment
 */
export const trackSignupAbandonment = () => {
  const state = getSignupFunnelState();
  
  // Only track if we have active signup state and it's not completed
  if (!state || state.completed) return;
  
  // Calculate time on funnel
  let timeOnFunnel = null;
  if (state.started_at) {
    const startTime = new Date(state.started_at);
    const now = new Date();
    timeOnFunnel = Math.round((now - startTime) / (1000 * 60));
  }
  
  const eventParams = {
    current_step: state.current_step,
    method: state.method || null,
    time_on_funnel_minutes: timeOnFunnel
  };
  
  try {
    pushToDataLayer('signup_abandoned', eventParams);
    logAnalyticsEvent('signup_abandoned', eventParams);
    
    // Clear funnel state after tracking abandonment
    clearSignupFunnel();
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};
